import { useEffect, useState } from "react";
import { api } from "@/lib/firebase/api-client";
import { intervalleVisible } from "@/lib/utils/intervalle-visible";

/**
 * ===========================================================================
 *  MES FOURNISSEURS SONT-ILS LA ?
 * ===========================================================================
 *
 *  Le responsable commande en a besoin toute la journee : une commande qui
 *  arrive chez un fournisseur deconnecte ne sera pas preparee, et c'est a
 *  lui de le voir AVANT que le coursier attende devant le magasin.
 *
 *  Les memes regles que le tableau de pilotage, pour que deux ecrans ne se
 *  contredisent jamais :
 *    vert   vu il y a moins de 10 minutes
 *    rouge  vu plus tot — avec l'heure, pour savoir s'il vient de partir
 *    gris   jamais connecte
 *
 *  Le serveur ne renvoie que les fournisseurs DU MAGASIN de celui qui
 *  regarde, et seulement leur nom et leur presence.
 * ===========================================================================
 */

type Fournisseur = {
  id: string;
  nom: string;
  vuLe: string | null;
  enService: boolean;
};

/** En dessous, il est la. Le meme seuil que le tableau de pilotage. */
const CONNECTE_MIN = 10;

function lire(vuLe: string | null) {
  if (!vuLe) return { etat: "jamais" as const, texte: "jamais connecté" };
  const minutes = Math.max(
    0,
    Math.round((Date.now() - new Date(vuLe).getTime()) / 60000),
  );
  if (minutes < CONNECTE_MIN) return { etat: "la" as const, texte: "connecté" };
  if (minutes < 60) return { etat: "parti" as const, texte: `vu il y a ${minutes} min` };
  const h = Math.floor(minutes / 60);
  if (h < 24) return { etat: "parti" as const, texte: `vu il y a ${h} h` };
  return {
    etat: "parti" as const,
    texte: `vu le ${new Date(vuLe).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: "Africa/Casablanca",
    })}`,
  };
}

export default function PresenceFournisseurs({ storeId }: { storeId?: string }) {
  const [liste, setListe] = useState<Fournisseur[] | null>(null);
  // « Il est ouvert » (29/10/2026) : remettre ses articles en vente.
  const [aConfirmer, setAConfirmer] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const [message, setMessage] = useState<{ ok: boolean; texte: string } | null>(null);

  async function ouvrir(f: Fournisseur) {
    setEnCours(f.id);
    setMessage(null);
    try {
      const r = await api.fournisseurs.ouvrirService(f.id);
      setListe((l) => (l || []).map((x) => (x.id === f.id ? { ...x, enService: true } : x)));
      setMessage({
        ok: r.glovoOk,
        texte: r.glovoOk
          ? `${f.nom} est ouvert : ${r.envoyes} article(s) remis en vente sur Glovo, Kooul et le site.`
          : `${f.nom} est ouvert, mais Glovo n'a pas tout accepté : ${r.erreur || "réessaie dans un instant"}.`,
      });
    } catch (e: any) {
      setMessage({ ok: false, texte: e?.message || "Impossible d'ouvrir ce fournisseur." });
    } finally {
      setEnCours(null);
      setAConfirmer(null);
    }
  }
  // Un nouveau rendu chaque minute : « vu il y a 8 min » doit vieillir.
  const [, setTic] = useState(0);

  useEffect(() => {
    let annule = false;
    async function charger() {
      try {
        const r = await api.fournisseurs.presence(storeId);
        if (!annule) setListe(r.fournisseurs || []);
      } catch {
        /* Le bloc est un confort : son absence ne casse pas la page. */
      }
    }
    void charger();
    // Onglet cache : plus d'appel au serveur ; retour : rafraichi aussitot.
    const arret = intervalleVisible(() => {
      void charger();
      setTic((t) => t + 1);
    }, 60000);
    return () => {
      annule = true;
      arret();
    };
  }, [storeId]);

  if (!liste || liste.length === 0) return null;

  const presents = liste.filter((f) => lire(f.vuLe).etat === "la").length;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-5">
      <div className="flex flex-wrap items-baseline gap-2 mb-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Mes fournisseurs
        </p>
        <span className="text-xs text-gray-400">
          {presents} connecté{presents > 1 ? "s" : ""} sur {liste.length}
        </span>
      </div>

      {message && (
        <p className={`mb-2 text-xs ${message.ok ? "text-green-700" : "text-red-700"}`}>{message.texte}</p>
      )}
      <div className="flex flex-wrap gap-2">
        {liste.map((f) => {
          const l = lire(f.vuLe);
          const couleur =
            l.etat === "la"
              ? "border-green-200 bg-green-50 text-green-700"
              : l.etat === "parti"
                ? "border-rose-200 bg-rose-50 text-rose-700"
                : "border-gray-200 bg-gray-50 text-gray-400";
          const point =
            l.etat === "la"
              ? "bg-green-500"
              : l.etat === "parti"
                ? "bg-red-500"
                : "bg-gray-300";
          return (
            <div
              key={f.id}
              className={`min-w-[150px] rounded-xl border px-3 py-2 ${couleur}`}
            >
              <p className="font-bold text-gray-900 text-sm">{f.nom}</p>
              <p className="text-xs mt-0.5 flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full shrink-0 ${point}`} />
                {l.texte}
              </p>
              {/* SERVICE FERME : il est peut-etre la, mais il ne prepare
                  pas. Fiable depuis la reouverture automatique du matin —
                  avant, tout le monde restait « ferme » toute la journee. */}
              {!f.enService && (
                <p className="text-[11px] font-bold text-amber-700 mt-0.5">
                  service fermé
                </p>
              )}
              {/* IL EST OUVERT : SES ARTICLES REVIENNENT EN VENTE
                  (Glovo, Kooul, site). Ouvrir seulement — fermer reste une
                  demande validee par le support. */}
              {aConfirmer === f.id ? (
                <div className="mt-1.5 flex items-center gap-1">
                  <button
                    type="button"
                    disabled={enCours === f.id}
                    onClick={() => void ouvrir(f)}
                    className="text-[11px] px-2 py-1 rounded-md bg-green-600 text-white font-semibold disabled:opacity-50"
                  >
                    {enCours === f.id ? "…" : "Confirmer"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setAConfirmer(null)}
                    className="text-[11px] px-1.5 py-1 text-gray-500"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setAConfirmer(f.id)}
                  className="mt-1.5 text-[11px] px-2 py-1 rounded-md border border-green-300 bg-white text-green-700 font-semibold hover:bg-green-50"
                >
                  ✓ Il est ouvert — activer ses articles
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
